import React from "react";
import { useDrop } from "react-dnd";
import { HiTrash } from "react-icons/hi2";
import {
  defaultColumnConfig,
  normalizePixelValue,
  normalizeTableConfig,
  normalizeTableElement,
} from "./table-config";

const BARCODE_IMAGE_PATTERN = /\.(png|jpe?g|webp|gif|bmp|svg)(\?.*)?$/i;

const isBarcodeImageUrl = (value) => {
  if (typeof value !== "string") {
    return false;
  }

  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return false;
  }

  if (!/^https?:\/\//i.test(trimmedValue)) {
    return false;
  }

  return (
    /barcode/i.test(trimmedValue) || BARCODE_IMAGE_PATTERN.test(trimmedValue)
  );
};

const isItemIdColumn = (column = {}) => {
  const columnIdentity = `${column?.fieldPlaceHolder || ""} ${
    column?.fieldName || ""
  }`.toLowerCase();

  return (
    columnIdentity.includes("itemid") ||
    columnIdentity.includes("item_id") ||
    columnIdentity.includes("item id") ||
    columnIdentity.includes("{item_id}")
  );
};

const renderCellValue = (column, cell) => {
  const value = cell?.fieldName || cell?.fieldPlaceHolder || "";
  const isImageCell = isItemIdColumn(column) && isBarcodeImageUrl(value);

  if (!isImageCell) {
    return value;
  }

  return (
    <img
      src={value.trim()}
      alt="Barcode"
      crossOrigin="anonymous"
      style={{
        display: "block",
        maxWidth: "100%",
        maxHeight: "48px",
        width: "auto",
        height: "auto",
        objectFit: "contain",
        margin: "0 auto",
      }}
    />
  );
};

const RenderTableHeaderCellCanvas = ({
  index,
  column,
  tableConfig,
  cellStyle,
  onAssignField,
  onDeleteColumn,
}) => {
  const [{}, drop] = useDrop(
    () => ({
      accept: ["NEW_ELEMENT"],
      canDrop: () => typeof onAssignField === "function",
      drop: (item, monitor) => {
        if (monitor.getItemType() === "NEW_ELEMENT" && onAssignField) {
          onAssignField({ ...item, id: Date.now() }, index);
        }
        return { handled: true };
      },
    }),
    [index, onAssignField],
  );

  const headerOverride =
    tableConfig?.headerTextOverride &&
    typeof tableConfig.headerTextOverride[index] !== "undefined"
      ? tableConfig.headerTextOverride[index]
      : "";
  const headerValue = headerOverride || column?.fieldName || column?.fieldPlaceHolder || "";
  const displayHeader =
    column?.uppercaseHeader || tableConfig?.uppercaseHeader
      ? String(headerValue).toUpperCase()
      : headerValue;

  return (
    <th ref={drop} className="builder-table-header-cell" style={cellStyle}>
      {displayHeader}
      <span className="builder-table-td-delete">
        <HiTrash onClick={() => onDeleteColumn && onDeleteColumn(index)} />
      </span>
    </th>
  );
};

const RenderTableHeaderCellStatic = ({ index, column, tableConfig, cellStyle }) => {
  const headerOverride =
    tableConfig?.headerTextOverride &&
    typeof tableConfig.headerTextOverride[index] !== "undefined"
      ? tableConfig.headerTextOverride[index]
      : "";
  const headerValue = headerOverride || column?.fieldName || column?.fieldPlaceHolder || "";
  const displayHeader =
    column?.uppercaseHeader || tableConfig?.uppercaseHeader
      ? String(headerValue).toUpperCase()
      : headerValue;

  return <th style={cellStyle}>{displayHeader}</th>;
};

const RenderTableElement = ({
  element,
  mode = "canvas",
  showPreviewRow = true,
  onAssignFieldToColumn,
  onDeleteColumn,
  onAddColumn,
}) => {
  const normalizedElement = normalizeTableElement(element);
  const tableConfig = normalizeTableConfig(normalizedElement.tableConfig);
  const columns = normalizedElement.columns || [];
  const rows =
    mode === "canvas" && showPreviewRow
      ? [
          {
            columns: columns.map((column) => ({
              fieldName: column.fieldName || column.fieldPlaceHolder || "",
              fieldPlaceHolder: column.fieldPlaceHolder || "",
            })),
          },
        ]
      : Array.isArray(normalizedElement.rows) && normalizedElement.rows.length > 0
        ? normalizedElement.rows
        : [
            {
              columns: columns.map(() => ({ fieldName: "", fieldPlaceHolder: "" })),
            },
          ];
  const totalBodyRows = rows.length;

  const hasVerySmallWidthColumn = columns.some((column) => {
    const parsed = parseInt(
      normalizePixelValue(column?.width, defaultColumnConfig.width).replace("px", ""),
      10,
    );
    return !Number.isNaN(parsed) && parsed > 0 && parsed < 80;
  });

  const getColumnSizeStyle = (widthValue) => {
    const normalizedWidth = normalizePixelValue(widthValue, defaultColumnConfig.width);
    const numericWidth = parseInt(normalizedWidth.replace("px", ""), 10);
    if (!Number.isNaN(numericWidth) && numericWidth < 80) {
      return {
        width: "auto",
        minWidth: `${numericWidth}px`,
      };
    }
    return {
      width: normalizedWidth,
      minWidth: normalizedWidth,
    };
  };

  const getCellBorder = (columnIndex, isHeader, rowIndex = 0) => {
    const width = `${tableConfig.borderWidth}px`;
    const style = tableConfig.borderStyle;
    const color = tableConfig.borderColor;
    const borderValue = `${width} ${style} ${color}`;
    const isFirstColumn = columnIndex === 0;
    const isLastColumn = columnIndex === columns.length - 1;
    const isLastBodyRow = rowIndex === totalBodyRows - 1;

    const borderStyles = {
      borderTop: "none",
      borderRight: "none",
      borderBottom: "none",
      borderLeft: "none",
    };

    if (tableConfig.rowBorders) {
      if (isHeader) {
        borderStyles.borderBottom = borderValue;
      } else if (!isLastBodyRow) {
        borderStyles.borderBottom = borderValue;
      }
    }

    if (isHeader && tableConfig.headerBorder) {
      borderStyles.borderTop = borderValue;
      borderStyles.borderBottom = borderValue;
    }

    if (tableConfig.columnDividers && !isLastColumn) {
      borderStyles.borderRight = borderValue;
    }

    if (tableConfig.outerBorder) {
      if (isHeader) {
        borderStyles.borderTop = borderValue;
      }
      if (!isHeader && isLastBodyRow) {
        borderStyles.borderBottom = borderValue;
      }
      if (isFirstColumn) {
        borderStyles.borderLeft = borderValue;
      }
      if (isLastColumn) {
        borderStyles.borderRight = borderValue;
      }
    }

    return borderStyles;
  };

  const getHeaderCellStyle = (column, index) => ({
    ...getCellBorder(index, true, 0),
    backgroundColor: tableConfig.headerBackground,
    color: tableConfig.headerTextColor,
    padding: `${tableConfig.cellPadding}px`,
    textAlign: column?.headerTextAlign || tableConfig.textAlign,
    fontFamily: tableConfig.fontFamily,
    fontSize: `${tableConfig.fontSize}px`,
    fontWeight: tableConfig.headerBold ? "bold" : "normal",
    textTransform: tableConfig.uppercaseHeader ? "uppercase" : "none",
    ...getColumnSizeStyle(column.width),
  });

  const getBodyCellStyle = (column, index, rowIndex) => ({
    ...getCellBorder(index, false, rowIndex),
    backgroundColor:
      tableConfig.stripedRows && rowIndex % 2 === 1
        ? tableConfig.alternateRowColor
        : tableConfig.bodyColor,
    color: tableConfig.bodyTextColor,
    padding: `${tableConfig.cellPadding}px`,
    textAlign: column?.textAlign || tableConfig.textAlign,
    fontFamily: tableConfig.fontFamily,
    fontSize: `${tableConfig.fontSize}px`,
    ...getColumnSizeStyle(column.width),
    overflowWrap: "anywhere",
  });

  return (
    <div
      style={{
        ...normalizedElement.styles,
        border:
          tableConfig.outerBorder && tableConfig.borderStyle !== "none"
            ? `${tableConfig.borderWidth}px ${tableConfig.borderStyle} ${tableConfig.borderColor}`
            : "none",
        borderRadius: `${tableConfig.borderRadius}px`,
        overflow: "hidden",
      }}
    >
      <table
        className="html-print-table"
        style={{
          width: "100%",
          tableLayout: hasVerySmallWidthColumn
            ? "auto"
            : normalizedElement.styles?.tableLayout ||
              normalizedElement.styles?.["table-layout"] ||
              "fixed",
          borderCollapse: "collapse",
        }}
      >
        <colgroup>
          {columns.map((column, index) => (
            <col key={`table-col-${index}`} style={getColumnSizeStyle(column.width)} />
          ))}
        </colgroup>
        <thead>
          <tr>
            {columns.map((column, index) => (
              mode === "canvas" ? (
                <RenderTableHeaderCellCanvas
                  key={`header-cell-${index}`}
                  index={index}
                  column={column}
                  tableConfig={tableConfig}
                  cellStyle={getHeaderCellStyle(column, index)}
                  onAssignField={onAssignFieldToColumn}
                  onDeleteColumn={onDeleteColumn}
                />
              ) : (
                <RenderTableHeaderCellStatic
                  key={`header-cell-${index}`}
                  index={index}
                  column={column}
                  tableConfig={tableConfig}
                  cellStyle={getHeaderCellStyle(column, index)}
                />
              )
            ))}
            {mode === "canvas" && (
              <th
                  style={{
                  ...getCellBorder(columns.length, true, 0),
                  backgroundColor: tableConfig.headerBackground,
                  color: tableConfig.headerTextColor,
                  padding: `${tableConfig.cellPadding}px`,
                  textAlign: tableConfig.textAlign,
                  fontFamily: tableConfig.fontFamily,
                  fontSize: `${tableConfig.fontSize}px`,
                  whiteSpace: "nowrap",
                }}
              >
                <button type="button" onClick={onAddColumn}>
                  Add Column
                </button>
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => {
            const rowColumns = Array.isArray(row?.columns)
              ? row.columns
              : Array.isArray(row)
                ? row
                : [];
            return (
              <tr key={`table-row-${rowIndex}`}>
                {columns.map((column, index) => {
                  const cell = rowColumns[index] || {};
                  return (
                    <td key={`table-cell-${rowIndex}-${index}`} style={getBodyCellStyle(column, index, rowIndex)}>
                      {renderCellValue(column, cell)}
                    </td>
                  );
                })}
                {mode === "canvas" && (
                  <td
                    style={{
                      ...getCellBorder(columns.length, false, rowIndex),
                      backgroundColor: tableConfig.bodyColor,
                      color: tableConfig.bodyTextColor,
                      padding: `${tableConfig.cellPadding}px`,
                      textAlign: tableConfig.textAlign,
                      fontFamily: tableConfig.fontFamily,
                      fontSize: `${tableConfig.fontSize}px`,
                    }}
                  />
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default RenderTableElement;
